import { PlusOutlined, ReloadOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, Radio, Row, Space, Spin, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ContentHeader } from '../../components';
import { Links } from '../../helpers/Links';
import { menuHelper } from '../../helpers/account';
import { NhanVienResponse } from '../../models/nhanvien/NhanVien';
import { NguoiPhuThuocToanDonVi, TimKiemNguoiPhuThuocRequest } from '../../models/nhanvien/NguoiPhuThuoc';
import { layCTNhanVien, timKiemNguoiPhuThuocToanDonVi, xuatExcelNguoiPhuThuocToanDonVi } from '../../services/nhanvien';
import DonViSelectTree from '../utils/DonViSelectTree';
import NguoiPhuThuocBox from './NguoiPhuThuocBox';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';

const NguoiPhuThuocPage = () => {
    const user = useSelector((state: any) => state.auth.currentUser);

    // Kiểm tra quyền theo cấu hình URL được phân quyền qua menuHelper
    const permissions: any = menuHelper.checkMultiPermission([
        Links.NguoiPhuThuoc.XemToanDonVi,
        Links.NguoiPhuThuoc.QuanLyToanDonVi,
    ]);
    const hasQuyenXemToanDonVi = !!(permissions[Links.NguoiPhuThuoc.XemToanDonVi] || user?.isAdmin);
    const hasQuyenQuanLyToanDonVi = !!(permissions[Links.NguoiPhuThuoc.QuanLyToanDonVi] || user?.isAdmin);

    const [activeTab, setActiveTab] = useState<string>("1");

    // State cho Tab 1: Người phụ thuộc
    const [loadingCaNhan, setLoadingCaNhan] = useState<boolean>(false);
    const [nhanVienCaNhan, setNhanVienCaNhan] = useState<NhanVienResponse>(new NhanVienResponse());

    // State cho Tab 2: Toàn đơn vị
    const [loadingToanDonVi, setLoadingToanDonVi] = useState<boolean>(false);
    const [xuatExcel, setXuatExcel] = useState<boolean>(false);
    const [dsToanDonVi, setDsToanDonVi] = useState<NguoiPhuThuocToanDonVi[]>([]);
    const [totalToanDonVi, setTotalToanDonVi] = useState<number>(0);
    const [pageToanDonVi, setPageToanDonVi] = useState<number>(1);
    const [pageSizeToanDonVi, setPageSizeToanDonVi] = useState<number>(10);
    const [filterToanDonVi, setFilterToanDonVi] = useState<TimKiemNguoiPhuThuocRequest>({ trangThai: 1 });
    const [formFilterToanDonVi] = Form.useForm();

    // Dữ liệu người phụ thuộc theo từng nhân viên khi mở rộng dòng (lazy load)
    const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
    const [chiTietTheoNhanVien, setChiTietTheoNhanVien] = useState<Record<number, NhanVienResponse>>({});
    const [loadingChiTiet, setLoadingChiTiet] = useState<Record<number, boolean>>({});

    const dispatch = useDispatch();
    const loadDataCaNhan = async () => {
        if (!user?.idNhanVien) {
            return;
        }
        setLoadingCaNhan(true);
        try {
            const rs = await layCTNhanVien(user.idNhanVien);
            setNhanVienCaNhan(rs);
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi tải thông tin người phụ thuộc của cá mhân");
        } finally {
            setLoadingCaNhan(false);
        }
    };

    const loadDataToanDonVi = async (page: number, size: number, filter: TimKiemNguoiPhuThuocRequest) => {
        setLoadingToanDonVi(true);
        try {
            const start = (page - 1) * size;
            const res = await timKiemNguoiPhuThuocToanDonVi(filter, start, size);
            setDsToanDonVi((res.data || []).map((item) => ({ ...item, key: item.id })));
            setTotalToanDonVi(res.recordsTotal || 0);
            setPageToanDonVi(page);
            const data = (res.data || []).map((item) => ({
                ...item,
                key: item.id,
            }));
            const dsCoNguoiPhuThuoc = data.filter(
                item => item.soLuongNguoiPhuThuoc > 0
            );

            setExpandedRowKeys(
                dsCoNguoiPhuThuoc.map(item => item.id)
            );

            // Chỉ tải chi tiết những nhân viên có người phụ thuộc
            await Promise.all(
                dsCoNguoiPhuThuoc.map(item =>
                    loadChiTietNhanVien(item.id)
                )
            );
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi tải danh sách người phụ thuộc toàn đơn vị");
        } finally {
            setLoadingToanDonVi(false);
        }
    };

    useEffect(() => {
        loadDataCaNhan();
    }, []);

    useEffect(() => {
        if (activeTab === "2" && hasQuyenXemToanDonVi) {
            loadDataToanDonVi(1, pageSizeToanDonVi, filterToanDonVi);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, hasQuyenXemToanDonVi]);
    const handleSearchToanDonVi = (values: any) => {
        const newFilter: TimKiemNguoiPhuThuocRequest = {
            tuKhoa: values.tuKhoa,
            idDonVi: values.idDonVi === 0 ? undefined : values.idDonVi,
            trangThai: values.trangThai,
        };
        setFilterToanDonVi(newFilter);
        loadDataToanDonVi(1, pageSizeToanDonVi, newFilter);
        if (xuatExcel) {
            setXuatExcel(false);
            handleExport(newFilter);
        }
    };

    const handleExport = async (newFilter: TimKiemNguoiPhuThuocRequest) => {
        dispatch(showLoadingScreen());
        try {
            const response = await xuatExcelNguoiPhuThuocToanDonVi(newFilter);
            const link = document.createElement('a');
            link.download = `ThongKeNguoiPhuThuocNam.xlsx`;
            const blob = new Blob([response.data], { type: response.data.type });
            link.href = URL.createObjectURL(blob);
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err: any) {
            toast.error('Xuất báo cáo không thành công. Vui lòng thử lại');
        } finally {
            dispatch(hideLoadingScreen());
        }
    };
    const handleResetFilterToanDonVi = () => {
        formFilterToanDonVi.resetFields();
        const newFilter: TimKiemNguoiPhuThuocRequest = { trangThai: 1 };
        setFilterToanDonVi(newFilter);
        loadDataToanDonVi(1, pageSizeToanDonVi, newFilter);
    };

    // Tải (hoặc tải lại) thông tin người phụ thuộc của 1 nhân viên khi mở rộng dòng
    const loadChiTietNhanVien = async (idNhanVien: number, forceReload?: boolean) => {
        if (!forceReload && chiTietTheoNhanVien[idNhanVien]) {
            return;
        }
        setLoadingChiTiet(prev => ({ ...prev, [idNhanVien]: true }));
        try {
            const rs = await layCTNhanVien(idNhanVien);
            setChiTietTheoNhanVien(prev => ({ ...prev, [idNhanVien]: rs }));
        } catch (error: any) {
            toast.error(error.message || "Lỗi khi tải thông tin người phụ thuộc của nhân viên");
        } finally {
            setLoadingChiTiet(prev => ({ ...prev, [idNhanVien]: false }));
        }
    };

    const columnsToanDonVi: ColumnsType<NguoiPhuThuocToanDonVi> = [
        {
            title: "STT",
            key: "stt",
            width: 60,
            align: 'center',
            render: (_, __, index) => (pageToanDonVi - 1) * pageSizeToanDonVi + index + 1,
        },
        {
            title: "Mã nhân viên",
            dataIndex: "ma",
            key: "ma",
            width: 130,
        },
        {
            title: "Họ tên nhân viên",
            dataIndex: "hoTen",
            key: "hoTen",
        },
        {
            title: "Chức vụ",
            dataIndex: "tenChucVu",
            key: "tenChucVu",
        },
        {
            title: "Phòng ban / Đơn vị",
            dataIndex: "tenDonVi",
            key: "tenDonVi",
        },
        {
            title: "Số người phụ thuộc",
            dataIndex: "soLuongNguoiPhuThuoc",
            key: "soLuongNguoiPhuThuoc",
            width: 160,
            align: 'center',
            render: (val) => <Tag color={val > 0 ? "blue" : "default"}>{val}</Tag>,
        },
    ];

    return (
        <div>
            <ContentHeader
                title="Người phụ thuộc"
            />

            <div className="content">
                <div className="container-fluid">
                    <Card style={{ marginBottom: 20 }}>
                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => setActiveTab(key)}
                            items={[
                                {
                                    key: "1",
                                    label: "Người phụ thuộc",
                                    children: loadingCaNhan ? (
                                        <div className="text-center" style={{ padding: 40 }}><Spin /></div>
                                    ) : (
                                        <NguoiPhuThuocBox
                                            listData={nhanVienCaNhan.nguoiPhuThuocs}
                                            listTaiLieu={nhanVienCaNhan.dsTaiLieuNguoiPhuThuocs}
                                            idNhanVien={user?.idNhanVien}
                                            isThemMoi
                                            onSubmit={loadDataCaNhan}
                                        />
                                    )
                                },
                                // Chỉ render Tab 2 nếu người dùng có quyền xem toàn đơn vị
                                ...(hasQuyenXemToanDonVi ? [
                                    {
                                        key: "2",
                                        label: "Toàn đơn vị",
                                        children: (
                                            <div>
                                                {/* Bộ lọc tìm kiếm Tab Toàn đơn vị */}
                                                <Form
                                                    form={formFilterToanDonVi}
                                                    onFinish={handleSearchToanDonVi}
                                                    layout="vertical"
                                                    initialValues={{ trangThai: 1 }}
                                                    style={{ marginBottom: 16 }}
                                                >
                                                    <Row gutter={16} align="bottom">
                                                        <Col xs={24} sm={12} md={8} lg={6}>
                                                            <Form.Item name="tuKhoa" label="Từ khóa" style={{ marginBottom: 8 }}>
                                                                <Input placeholder="Tên, mã nhân viên..." allowClear />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={12} md={8} lg={6}>
                                                            <Form.Item name="idDonVi" label="Phòng ban / Đơn vị" style={{ marginBottom: 8 }}>
                                                                <DonViSelectTree
                                                                    optionAll
                                                                    autoSelectFirst={false}
                                                                    allowClear
                                                                    onChange={(e: any) => {
                                                                        formFilterToanDonVi.setFieldsValue({ idDonVi: e?.target?.value });
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={12} md={8} lg={6}>
                                                            <Form.Item name="trangThai" label="Trạng thái" style={{ marginBottom: 8 }}>
                                                                <Radio.Group buttonStyle="solid">
                                                                    <Radio.Button value={1}>Đang làm việc</Radio.Button>
                                                                    <Radio.Button value={null}>Tất cả</Radio.Button>
                                                                </Radio.Group>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={24} md={24} lg={6} style={{ marginBottom: 8 }}>
                                                            <Space wrap>
                                                                <Button
                                                                    className="hrm-btn hrm-btn-success"
                                                                    icon={<DownloadOutlined />}
                                                                    onClick={() => { setXuatExcel(true); }}
                                                                    htmlType="submit" 
                                                                >
                                                                    Xuất excel
                                                                </Button>
                                                                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                                                                    Tìm kiếm
                                                                </Button>
                                                                <Button icon={<ReloadOutlined />} onClick={handleResetFilterToanDonVi}>
                                                                    Làm mới
                                                                </Button>
                                                            </Space>
                                                        </Col>
                                                    </Row>
                                                </Form>

                                                {/* Bảng danh sách nhân viên, mở rộng để xem/quản lý người phụ thuộc */}
                                                <Table
                                                    columns={columnsToanDonVi}
                                                    dataSource={dsToanDonVi}
                                                    loading={loadingToanDonVi}
                                                    rowKey="id"
                                                    pagination={{
                                                        current: pageToanDonVi,
                                                        pageSize: pageSizeToanDonVi,
                                                        total: totalToanDonVi,
                                                        showSizeChanger: true,
                                                        pageSizeOptions: ['10', '20', '50'],
                                                        showTotal: (total) => `Tổng cộng ${total} nhân viên`,
                                                        onChange: (page, size) => {
                                                            setPageSizeToanDonVi(size);
                                                            loadDataToanDonVi(page, size, filterToanDonVi);
                                                        }
                                                    }}
                                                    expandable={{
                                                        expandedRowKeys,
                                                        onExpand: (expanded, record) => {
                                                            if (expanded) {
                                                                setExpandedRowKeys(prev => [...prev, record.id]);
                                                                loadChiTietNhanVien(record.id);
                                                            } else {
                                                                setExpandedRowKeys(prev => prev.filter(k => k !== record.id));
                                                            }
                                                        },
                                                        expandedRowRender: (record) => (
                                                            loadingChiTiet[record.id] ? (
                                                                <div className="text-center" style={{ padding: 20 }}><Spin /></div>
                                                            ) : (
                                                                <NguoiPhuThuocBox
                                                                    listData={chiTietTheoNhanVien[record.id]?.nguoiPhuThuocs}
                                                                    listTaiLieu={chiTietTheoNhanVien[record.id]?.dsTaiLieuNguoiPhuThuocs}
                                                                    idNhanVien={record.id}
                                                                    isThemMoi={hasQuyenQuanLyToanDonVi}
                                                                    onSubmit={() => {
                                                                        loadChiTietNhanVien(record.id, true);
                                                                        loadDataToanDonVi(pageToanDonVi, pageSizeToanDonVi, filterToanDonVi);
                                                                    }}
                                                                />
                                                            )
                                                        )
                                                    }}
                                                    bordered
                                                    size="middle"
                                                    scroll={{ x: 1000 }}
                                                />
                                            </div>
                                        )
                                    }
                                ] : [])
                            ]}
                        />
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default NguoiPhuThuocPage;
